/**
 * Building blocks for the Sales insight pages (Executive Summary, Fair Share,
 * Price & Distribution, Weekly Trend): dataset access, number formatting for
 * tiles, the tile and panel chrome, and chart styling that matches the generic
 * adapters (same fonts, muted axes, faint grid lines).
 */

import {
  NEGATIVE,
  POSITIVE,
  axisLabel,
  axisLine,
  baseOption,
  departmentAccent,
  seriesPalette,
  splitLine,
} from '../../lib/charts'
import { formatCompact, formatPercent } from '../../lib/format'
import type { Cell, ReportResult } from '../../lib/types'

export type Row = Readonly<Record<string, Cell>>

/** A dataset's rows as objects keyed by column key (``primary`` when omitted). */
export function records(result: ReportResult, dataset?: string): Row[] {
  const source =
    dataset === undefined ? { columns: result.columns, rows: result.rows } : result.datasets[dataset]
  if (source === undefined) return []
  return source.rows.map((row) =>
    Object.fromEntries(source.columns.map((column, i) => [column.key, row[i] ?? null])),
  )
}

export function num(value: Cell | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export function money(value: Cell | undefined): string {
  const n = num(value)
  return n === null ? '—' : `$${formatCompact(n, n !== 0 && Math.abs(n) < 1e6 ? 1 : 2)}`
}

export function pct(value: Cell | undefined, decimals = 1): string {
  const n = num(value)
  return n === null ? '—' : formatPercent(n, decimals)
}

export function signedPct(value: Cell | undefined, decimals = 1): string {
  const n = num(value)
  if (n === null) return '—'
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${formatPercent(Math.abs(n), decimals)}`
}

export function signedPts(value: Cell | undefined): string {
  const n = num(value)
  if (n === null) return '—'
  return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(1)} pts`
}

export function toneOf(value: Cell | undefined): 'up' | 'down' | 'flat' {
  const n = num(value)
  if (n === null || Math.abs(n) < 1e-9) return 'flat'
  return n > 0 ? 'up' : 'down'
}

/** Chart colours for a department: accent first, then the neutral sequence. */
export function palette(department: string, count: number): string[] {
  return seriesPalette(departmentAccent(department), count)
}

/** Named chart colours, always defined. */
export function colorsFor(department: string): {
  accent: string
  second: string
  third: string
  fourth: string
  list: (n: number) => string[]
} {
  const accent = departmentAccent(department)
  const p = seriesPalette(accent, 6)
  return {
    accent,
    second: p[1] ?? accent,
    third: p[2] ?? accent,
    fourth: p[3] ?? accent,
    list: (n: number) => seriesPalette(accent, Math.max(n, 1)),
  }
}

export function colorAt(list: readonly string[], i: number, fallback = '#888888'): string {
  return list[i] ?? fallback
}

export const GOOD = POSITIVE
export const BAD = NEGATIVE

export const chart = { baseOption, axisLabel, axisLine, splitLine }

export function compactAxis(value: number): string {
  return `$${formatCompact(value, 1)}`
}

export function pctAxis(value: number): string {
  return formatPercent(value, 0)
}
